import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Advert } from 'interfaces/common';
import { AdvertService } from 'services/common';
import { FilterData, Pagination } from 'interfaces/core';
import { toPreviewUrl } from 'helpers';
import { AdvertStatus } from 'enum/advert-status.enum';

// add more keys if needed
interface QueryParams {
  searchQuery?: string;
  bodyTypes?: string | Array<string>;
  categories?: string | Array<string>;
  divisions?: string | Array<string>;
  hairColors?: string | Array<string>;
  intimateHairs?: string | Array<string>;
  orientations?: string | Array<string>;
  types?: string | Array<string>;
  area?: string | Array<string>;
  zone?: string | Array<string>;
  height?: string;
  weight?: string;
  age?: string;
  isGoingOut?: 'true' | 'false';
  acceptsHandicapped?: 'true' | 'false';
  ownPlace?: 'true' | 'false';
  verified?: 'true' | 'false';
}

type ComplexFilter<T extends string = string> = Array<
  Record<T, { slug: string }>
>;
type SizeKeys = 'height' | 'weight' | 'age';
type SizeFilter<K extends SizeKeys> = Array<
  Record<K, { $lte: number; $gte: number }>
>;

@Component({
  selector: 'app-product-list-right',
  templateUrl: './product-list-right.component.html',
  styleUrls: ['./product-list-right.component.scss'],
})
export class ProductListRightComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public title!: string;
  @Input() public description?: string;
  @Input() public showBreadcrumbs = false;
  @Input() private vipOnly = false;
  @Input({ required: true }) private filter!: FilterData;

  //Store data
  public productData: Array<Advert> = [];
  private searchQuery?: string;
  public areaData?: string;
  public divisionData?: string;
  public zoneData?: string;

  private currentPage = 1;
  public totalAdverts = 0;
  private advertsPerPage = 18;

  public isLoadMore = false;
  public showLoadMore: boolean = true;

  // Complex Filter
  private categoryFilter: ComplexFilter<'category'> = [];
  private typeFilter: ComplexFilter<'type'> = [];
  private intimateHairsFilter: ComplexFilter<'intimateHair'> = [];
  private bodyTypesFilter: ComplexFilter<'bodyType'> = [];
  private orientationsFilter: ComplexFilter<'orientation'> = [];
  private hairColorsFilter: ComplexFilter<'hairColor'> = [];
  private divisionFilter: ComplexFilter<'division'> = [];
  private zoneFilter: ComplexFilter<'zone'> = [];
  private areaFilter: ComplexFilter<'area'> = [];
  private heightFilter: SizeFilter<'height'> = [];
  private weightFilter: SizeFilter<'weight'> = [];
  private ageFilter: SizeFilter<'age'> = [];
  private verified = false;
  private acceptsHandicapped = false;
  private isGoingOut = false;
  private ownPlace = false;

  // Subscriptions
  private subFilterProduct: Subscription = Subscription.EMPTY;
  private subVIPFilterProduct: Subscription = Subscription.EMPTY;
  private subSearchProduct: Subscription = Subscription.EMPTY;

  constructor(
    private activatedRoute: ActivatedRoute,
    private advertService: AdvertService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(qParam => {
      // this.location = qParam['category'];
      this.divisionData = qParam['divisions'];
      this.areaData = qParam['area'];
      this.zoneData = qParam['zone'];

      if (Object.values(qParam).length !== 0) {
        this.currentPage = 1;
        this.searchQuery = qParam['searchQuery'];
      } else {
        this.verified = false;
      }
      // This also resets filters (on clear filter click)
      this.filterQueryFromQueryParam(qParam);
      // Fetch data
      this.vipOnly ? this.getAllVIPAdverts() : this.getAllAdverts();
    });
  }

  /**
   * QUERY BUILDER
   * filterQueryFromQueryParam()
   */

  private filterQueryFromQueryParam(q: QueryParams) {
    this.categoryFilter = this.toComplexFilter('category', q.categories);
    this.typeFilter = this.toComplexFilter('type', q.types);
    this.intimateHairsFilter = this.toComplexFilter(
      'intimateHair',
      q.intimateHairs
    );
    this.bodyTypesFilter = this.toComplexFilter('bodyType', q.bodyTypes);
    this.orientationsFilter = this.toComplexFilter(
      'orientation',
      q.orientations
    );
    this.hairColorsFilter = this.toComplexFilter('hairColor', q.hairColors);
    this.divisionFilter = this.toComplexFilter('division', q.divisions);
    this.zoneFilter = this.toComplexFilter('zone', q.zone);
    this.areaFilter = this.toComplexFilter('area', q.area);
    this.heightFilter = this.toSizeFilter('height', q.height);
    this.weightFilter = this.toSizeFilter('weight', q.weight);
    this.ageFilter = this.toSizeFilter('age', q.age);

    const keys = [
      'isGoingOut',
      'acceptsHandicapped',
      'ownPlace',
      'verified',
    ] as const;
    keys.forEach(prop => {
      this[prop] = q[prop] === 'true';
    });
  }

  private toSizeFilter<K extends SizeKeys>(
    key: K,
    value?: string
  ): SizeFilter<K> {
    if (!value) return [];
    const [min, max] = value.split('-').map(Number);
    // type assertion is needed because [key] becomes string
    return [{ [key]: { $lte: max, $gte: min } }] as SizeFilter<K>;
  }
  private toComplexFilter<K extends string>(
    key: K,
    value?: string | Array<string>
  ) {
    if (!value) return [];
    // type assertion is needed because `[key]` becomes string
    return new Array<string>()
      .concat(value)
      .map(p => ({ [key]: { slug: p } })) as ComplexFilter<K>;
  }

  private getAllAdverts(loadMore?: boolean) {
    const pagination: Pagination = this.generatePagination();
    const mSelect = this.generateSelectFields();
    const comFilter = this.generateComplexFilter();
    const mFilter = this.filter.filter ? { ...this.filter } : { ...comFilter };

    const filterData: FilterData = {
      pagination: pagination,
      filter: { ...mFilter, isReviewed: true },
      select: mSelect,
      sort: [
        { bumpDate: 'desc' },
        { publishDate: 'desc' },
        { createdAt: 'desc' },
      ],
    };

    this.subFilterProduct = this.advertService
      .getAllCombinedAdverts(filterData, this.searchQuery)
      .subscribe(
        res => {
          this.isLoadMore = false;
          this.resetFilters();
          const resAdverts = res.data;
          this.productData = loadMore
            ? this.productData.concat(resAdverts)
            : resAdverts;
          this.productData.forEach(product => {
            product.images = product.images?.map(toPreviewUrl);
          });
          this.totalAdverts = res.count;
          this.showLoadMore = res?.data?.length > 0;
        },
        error => {
          this.currentPage -= 1;
          console.log(error);
        }
      );
  }

  private getAllVIPAdverts(loadMore?: boolean) {
    const pagination: Pagination = this.generatePagination();
    const mSelect = this.generateSelectFields();
    const comFilter = this.generateComplexFilter();
    const mFilter = { ...comFilter, isVip: true };

    const filterData: FilterData = {
      pagination: pagination,
      filter: { ...mFilter, isReviewed: true },
      select: mSelect,
      sort: [
        { bumpDate: 'desc' },
        { publishDate: 'desc' },
        { createdAt: 'desc' },
      ],
    };
    this.subVIPFilterProduct = this.advertService
      .getAllAdverts(filterData, this.searchQuery)
      .subscribe(
        res => {
          const resAdverts = res.data as Array<Advert>;
          this.productData = loadMore
            ? this.productData.concat(resAdverts)
            : resAdverts;
          this.productData.forEach(product => {
            product.images = product.images?.map(toPreviewUrl);
          });
          this.totalAdverts = res.count;
          this.showLoadMore = res?.data?.length > 0;
        },
        error => {
          this.currentPage -= 1;
          console.log(error);
        }
      );
  }

  private generatePagination() {
    return {
      limit: Number(this.advertsPerPage),
      page: Number(this.currentPage) - 1,
    };
  }

  private generateSelectFields() {
    return {
      name: true,
      slug: true,
      images: true,
      category: true,
      user: true,
      status: true,
      type: true,
      age: true,
      division: true,
      area: true,
      zone: true,
    };
  }

  private resetFilters() {
    this.categoryFilter = [];
    this.typeFilter = [];
    this.divisionFilter = [];
    this.areaFilter = [];
    this.zoneFilter = [];
    this.intimateHairsFilter = [];
    this.bodyTypesFilter = [];
    this.orientationsFilter = [];
    this.hairColorsFilter = [];
    this.heightFilter = [];
    this.weightFilter = [];
    this.ageFilter = [];
    this.isGoingOut = false;
    this.acceptsHandicapped = false;
    this.ownPlace = false;
  }

  private generateComplexFilter() {
    const compFilter = [
      this.categoryFilter,
      this.typeFilter,
      this.intimateHairsFilter,
      this.bodyTypesFilter,
      this.hairColorsFilter,
      this.orientationsFilter,
      this.divisionFilter,
      this.areaFilter,
      this.zoneFilter,
      this.heightFilter,
      this.weightFilter,
      this.ageFilter,
    ].reduce(
      (obj, filters) => {
        const filter = filters.at(0);
        if (!filter) return obj;

        const [key, value] = Object.entries(filter)[0];
        if (value) {
          obj[key] = value;
        }
        return obj;
      },
      { status: AdvertStatus.Publish } as Record<string, string | boolean>
    );

    compFilter['verified'] = this.verified;
    const keys = ['isGoingOut', 'acceptsHandicapped', 'ownPlace'] as const;
    keys.forEach(prop => {
      if (this[prop]) {
        compFilter[prop] = true;
      }
    });

    return compFilter;
  }

  /**
   * LOAD MORE
   */
  public onLoadMore() {
    if (this.totalAdverts > this.productData.length) {
      this.isLoadMore = true;
      this.currentPage += 1;
      if (this.vipOnly) {
        this.getAllVIPAdverts(true);
      } else if (!this.vipOnly) {
        this.getAllAdverts(true);
      }
    }
  }

  /**
   * Destry Subscription
   * onHideFilterArea();
   */
  ngOnDestroy(): void {
    this.subFilterProduct.unsubscribe();
    this.subVIPFilterProduct.unsubscribe();
    this.subSearchProduct.unsubscribe();
  }
}
